if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.mql = window.matchMedia('(min-width: 750px)');
        this._boundSlideChanged = null;
        this._boundThumbnailClick = null;
      }

      _getElements() {
        return {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
      }

      connectedCallback() {
        this._subscribeVariantChange();
        requestAnimationFrame(() => this._init());
      }

      _subscribeVariantChange() {
        if (typeof subscribe === 'undefined' || typeof PUB_SUB_EVENTS === 'undefined') return;
        if (window.__mediaGalleryVariantChangeSubscribed) return;
        window.__mediaGalleryVariantChangeSubscribed = true;
        subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          const { sectionId, variant } = event?.data || {};
          if (!variant?.featured_media?.id || !sectionId) return;
          const mediaId = `${sectionId}-${variant.featured_media.id}`;
          document.querySelectorAll('media-gallery').forEach((gallery) => {
            const gid = gallery.id || '';
            if (gid.replace('MediaGallery-', '') === sectionId && gallery.setActiveMedia) {
              gallery.setActiveMedia(mediaId, true);
            }
          });
        });
      }

      _init() {
        this.elements = this._getElements();
        if (!this.elements.thumbnails || !this.elements.viewer) return;

        if (this._boundSlideChanged) return;
        this._boundSlideChanged = true;

        const debouncedSlideChanged = typeof debounce === 'function'
          ? debounce(this.onSlideChanged.bind(this), 500)
          : this.onSlideChanged.bind(this);
        this.elements.viewer.addEventListener('slideChanged', debouncedSlideChanged);

        /* Thumbnail-Klick: wie im Backup – direkte Bindung an jeden Button */
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((el) => {
          const btn = el.querySelector('button');
          if (!btn) return;
          btn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            const mediaId = el.getAttribute('data-target');
            if (mediaId) this.setActiveMedia(mediaId, false);
          });
        });

        /* Pfeil oben/unten: Wenn der Thumbnail-Slider die Slide wechselt (Klick auf Pfeil), Hauptbild aktualisieren */
        this.elements.thumbnails.addEventListener('slideChanged', (e) => {
          const slide = e.detail && e.detail.currentElement;
          if (!slide || !slide.getAttribute('data-target')) return;
          const mediaId = slide.getAttribute('data-target');
          this.setActiveMedia(mediaId, false);
        });

        /* Direkt auf Pfeil-Buttons klicken: Nach dem Scroll das sichtbare Thumbnail ermitteln und Hauptbild setzen */
        const thumbSlider = this.elements.thumbnails.slider;
        if (thumbSlider) {
          const onThumbScroll = () => {
            if (!this.elements.thumbnails || !this.elements.thumbnails.sliderItemsToShow || this.elements.thumbnails.sliderItemsToShow.length === 0) return;
            const scrollTop = thumbSlider.scrollTop;
            const offset = this.elements.thumbnails.sliderItemOffset || 100;
            const index = Math.min(
              Math.round(scrollTop / offset),
              this.elements.thumbnails.sliderItemsToShow.length - 1
            );
            const slide = this.elements.thumbnails.sliderItemsToShow[Math.max(0, index)];
            if (slide && slide.getAttribute('data-target')) {
              this.setActiveMedia(slide.getAttribute('data-target'), false);
            }
          };
          thumbSlider.addEventListener('scroll', () => {
            window.clearTimeout(this._thumbScrollTimeout);
            this._thumbScrollTimeout = window.setTimeout(onThumbScroll, 150);
          });
        }

        if (this.dataset.desktopLayout && this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            /* Null-Guard: Im Quick-Add-Modal kann product-info.updateMedia waehrend des
               Variantenwechsels setActiveMedia mit einer mediaId der NEUEN Variante aufrufen,
               waehrend die Galerie noch die alte Variante zeigt. Da Variantenbilder anderer
               Groessen nicht mehr als (verstecktes) Thumbnail gerendert werden, ist
               activeThumbnail dann null. Ohne Guard wirft .parentElement und der
               product-info-Callback bricht ab -> der Add-to-Cart-Button bleibt disabled. */
            if (activeThumbnail && activeThumbnail.parentElement.firstChild !== activeThumbnail) {
              activeThumbnail.parentElement.prepend(activeThumbnail);
            }
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        if (activeThumbnail) this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition || '1');
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        const isVertical = this.dataset.thumbnailVertical === 'true';
        if (isVertical && this.elements.thumbnails.slider) {
          this.elements.thumbnails.slider.scrollTo({ top: thumbnail.offsetTop, behavior: 'smooth' });
        } else if (this.elements.thumbnails.slider) {
          this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
        }
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        // Pausiere Videos nur wenn nicht im Quick-Add-Modal (damit Videos weiterlaufen)
        if (!this.closest('quick-add-modal')) {
          window.pauseAllMedia();
        }
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
